import { useEffect, useMemo, useState } from "react";
import { deleteDoc, doc, onSnapshot } from "firebase/firestore";
import { db } from "../lib/firebase";
import { useAuth } from "../hooks/useAuth";
import { useCouple } from "../hooks/useCouple";
import type { LoveNote } from "../types";

const HEART_EMOJI = ["💕", "💖", "❤️", "✨", "💗"];

export function LoveNoteOverlay() {
  const { user } = useAuth();
  const { couple } = useCouple();
  const [note, setNote] = useState<LoveNote | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!couple || !user) return;
    return onSnapshot(doc(db, "couples", couple.id, "loveNotes", user.uid), (snap) => {
      if (snap.exists()) {
        setNote(snap.data() as LoveNote);
        requestAnimationFrame(() => setVisible(true));
      }
    });
  }, [couple, user]);

  // Regenerated once per note (not per render) so the hearts don't jump
  // around mid-animation — a fixed little flourish for this one reveal.
  const hearts = useMemo(
    () =>
      Array.from({ length: 10 }, (_, i) => ({
        emoji: HEART_EMOJI[i % HEART_EMOJI.length],
        left: 5 + Math.random() * 90,
        delay: Math.random() * 1.2,
        duration: 3 + Math.random() * 2,
        size: 1 + Math.random() * 1.2,
      })),
    [note]
  );

  function dismiss() {
    setVisible(false);
    setTimeout(async () => {
      if (couple && user) await deleteDoc(doc(db, "couples", couple.id, "loveNotes", user.uid));
      setNote(null);
    }, 300);
  }

  if (!note) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-6">
      <div
        className="absolute inset-0 bg-plum-900/40 backdrop-blur-sm transition-opacity duration-300"
        style={{ opacity: visible ? 1 : 0 }}
        onClick={dismiss}
      />
      {visible && (
        <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
          {hearts.map((h, i) => (
            <span
              key={i}
              className="love-note-heart absolute bottom-0"
              style={{
                left: `${h.left}%`,
                fontSize: `${h.size}rem`,
                animationDelay: `${h.delay}s`,
                animationDuration: `${h.duration}s`,
              }}
            >
              {h.emoji}
            </span>
          ))}
        </div>
      )}
      {/* The whole card flies in from far away as one piece — starts tiny
          and faint, eases out to full size, rather than just the text. */}
      <div
        className="relative w-full max-w-sm overflow-hidden rounded-[2rem] border border-white/60 bg-white/35 p-7 text-center shadow-[0_24px_60px_rgba(43,29,35,0.35)] backdrop-blur-2xl backdrop-saturate-150 transition-all ease-out"
        style={{
          opacity: visible ? 1 : 0,
          transform: visible ? "scale(1) translateY(0)" : "scale(0.25) translateY(30px)",
          transitionDuration: "750ms",
        }}
      >
        <p className={`text-4xl ${visible ? "love-note-bounce" : ""}`}>💌</p>
        <p className="mt-3 text-xs font-medium uppercase tracking-wider text-ink-600">Von {note.fromName}</p>
        <p className="mt-3 whitespace-pre-wrap font-display text-xl leading-snug text-plum-900">{note.text}</p>
        <button onClick={dismiss} className="mt-6 h-11 w-full rounded-xl bg-plum-700/90 text-sm font-semibold text-cream-50 backdrop-blur">
          Schließen
        </button>
      </div>
    </div>
  );
}
